import { Flex } from 'antd';

import { SPACE_MD } from '@/theme/buildAntdTheme';
import Card from '@/UI/Card/Card';
import Empty, { type EmptyListingConfig } from '@/UI/Empty/Empty';
import Loader from '@/UI/Loader';

import SmartPagination from './SmartPagination';

import type { SmartPaginationProps } from './SmartPagination';
import type React from 'react';

type FilteredListingShellProps = {
  header?: React.ReactNode;
  secondaryToolbar: React.ReactNode;
  pagination?: SmartPaginationProps | false | null;
  loading?: boolean;
  resultCount?: number;
  empty?: EmptyListingConfig | null;
  showEmptyClear?: boolean;
  onClearAllFilters?: () => void;
  emptyTestId?: string;
  initialLoadingPlaceholder?: React.ReactNode;
  children: React.ReactNode;
};

export function FilteredListingShell({
  header,
  secondaryToolbar,
  pagination,
  loading = false,
  resultCount,
  empty,
  showEmptyClear,
  onClearAllFilters,
  emptyTestId,
  initialLoadingPlaceholder,
  children,
}: FilteredListingShellProps) {
  const showPagination = pagination != null && pagination !== false;
  const isEmpty = !loading && resultCount === 0 && empty != null;
  const showLoadingPlaceholder =
    loading &&
    !isEmpty &&
    (resultCount ?? 0) === 0 &&
    initialLoadingPlaceholder != null;

  return (
    <Card styles={{ root: { minHeight: '100%' }, body: { minHeight: '100%' } }}>
      <Flex vertical gap={SPACE_MD}>
        {header != null && <>{header}</>}
        {secondaryToolbar != null && (
          <Flex vertical data-testid="rm-listing-secondary-toolbar">
            {secondaryToolbar}
          </Flex>
        )}
        <Flex
          vertical
          data-testid="filtered-listing-shell-scroll"
          style={{
            overflowX: 'hidden',
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {isEmpty ? (
            <Empty
              data-testid={emptyTestId ?? 'filtered-listing-empty-state'}
              description={empty.description}
              clearLabel={empty.clearLabel}
              showClear={showEmptyClear}
              onClear={onClearAllFilters}
            />
          ) : showLoadingPlaceholder ? (
            initialLoadingPlaceholder
          ) : (
            <Loader size="large" spinning={loading}>
              <Flex vertical>{children}</Flex>
            </Loader>
          )}
          {showPagination && !isEmpty && <SmartPagination {...pagination} />}
        </Flex>
      </Flex>
    </Card>
  );
}
